"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShoplandingLogin = void 0;
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const common_types_1 = require("@as-react/common-types");
const common_components_1 = require("@as-react/common-components");
const common_queries_1 = require("@as-react/common-queries");
const react_query_1 = require("@tanstack/react-query");
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const react_i18next_1 = require("react-i18next");
const awesome_debounce_promise_1 = tslib_1.__importDefault(require("awesome-debounce-promise"));
const Yup = tslib_1.__importStar(require("yup"));
const i18nConstants_1 = require("../../../../constants/i18nConstants");
const EmailValidationUtil_1 = require("../../../email/EmailValidationUtil");
const FormValidationUtil_1 = tslib_1.__importDefault(require("../../../../util/FormValidationUtil"));
const ValueChangeWatcher_1 = require("../../../form/watchers/ValueChangeWatcher");
const AuthActions_creators_1 = require("../../../../redux/authentication/actions/AuthActions.creators");
const AuthSelectors_1 = require("../../../../redux/authentication/selectors/AuthSelectors");
const ShoplandingLoginForm_1 = require("./ShoplandingLoginForm");
const defaultCardInfo = {
    customerUUID: null,
    email: null,
    validEmail: false,
    existingCustomer: false,
};
const ShoplandingLogin = () => {
    const { defaultRequestParams, servicesEndpoint, checkoutLoyaltyCardPrefix } = (0, common_components_1.useAemConfig)();
    const { t } = (0, react_i18next_1.useTranslation)([i18nConstants_1.I18N_NAMESPACES.checkout, i18nConstants_1.I18N_NAMESPACES.buy, i18nConstants_1.I18N_NAMESPACES.account]);
    const [customerCardKnown, setCustomerCardKnown] = (0, react_1.useState)(false);
    const [customerCardInfo, setCustomerCardInfo] = (0, react_1.useState)(defaultCardInfo);
    const [linkedEmail, setLinkedEmail] = (0, react_1.useState)('');
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [emailOrCardCodeFieldValue, setEmailOrCardCodeFieldValue] = (0, react_1.useState)();
    const [emailOrCardCodeStatus, setEmailOrCardCodeStatus] = (0, react_1.useState)();
    const [linkedEmailStatus, setLinkedEmailStatus] = (0, react_1.useState)();
    const [linkedEmailFieldValue, setLinkedEmailFieldValue] = (0, react_1.useState)();
    const [suggestedEmail, setSuggestedEmail] = (0, react_1.useState)();
    const [skipEmailValidation, setSkipEmailValidation] = (0, react_1.useState)(false);
    const dispatch = (0, react_redux_1.useDispatch)();
    const guestError = (0, react_redux_1.useSelector)((0, AuthSelectors_1.makeGetErrorGuestState)());
    const errorFeedback = guestError ? t(guestError) : undefined;
    const queryClient = (0, react_query_1.useQueryClient)();
    const { getCardNumberExists, getEmailExists } = (0, common_queries_1.customerQueries)({ servicesEndpoint, defaultRequestParams });
    const getCustomerInfoForExploreMoreCard = (customerCardNumber) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield queryClient.fetchQuery(getCardNumberExists(customerCardNumber));
        }
        catch (_a) {
            return defaultCardInfo;
        }
    });
    const getEmailStatus = (email) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield queryClient.fetchQuery(getEmailExists({ email }));
        }
        catch (_b) {
            return { accountStatusCode: common_types_1.AuthStatusCode.NO_ACCOUNT, knownForFascia: false, hasIdentity: false };
        }
    });
    const validateEmailOrCardCode = (0, react_1.useCallback)((0, awesome_debounce_promise_1.default)((emailOrCardCode, context) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
        var _c;
        if (skipEmailValidation) {
            return true;
        }
        if (emailOrCardCode) {
            const { isValidFormat, isDefaultCardNumber } = FormValidationUtil_1.default.isValidExploreMoreCardFormat(emailOrCardCode, checkoutLoyaltyCardPrefix);
            if (isValidFormat && isDefaultCardNumber) {
                return context.createError({
                    message: t(`${i18nConstants_1.I18N_NAMESPACES.checkout}:checkout.login.error.loyalty.card.default`),
                });
            }
            else if (isValidFormat && !isDefaultCardNumber) {
                const cardInfo = yield getCustomerInfoForExploreMoreCard(emailOrCardCode);
                setCustomerCardInfo(cardInfo);
                setCustomerCardKnown(cardInfo.existingCustomer);
                if (!cardInfo.existingCustomer) {
                    return context.createError({
                        message: t(`${i18nConstants_1.I18N_NAMESPACES.checkout}:checkout.login.error.loyalty.card`),
                    });
                }
                else if (cardInfo.validEmail) {
                    setLinkedEmail((_c = cardInfo.email) !== null && _c !== void 0 ? _c : undefined);
                }
                else if (!cardInfo.validEmail) {
                    setLinkedEmail('');
                }
            }
            else if (!isValidFormat) {
                if (!FormValidationUtil_1.default.isValidEmailPattern(emailOrCardCode)) {
                    return context.createError({ message: t(`${i18nConstants_1.I18N_NAMESPACES.buy}:buy.error.invalid`) });
                }
                else {
                    return yield (0, EmailValidationUtil_1.validateEmailWithBackend)(emailOrCardCode, context, servicesEndpoint, emailOrCardCodeStatus, setSuggestedEmail, setEmailOrCardCodeStatus);
                }
            }
        }
        else if (!emailOrCardCode) {
            setCustomerCardKnown(false);
            return context.createError({ message: t(`${i18nConstants_1.I18N_NAMESPACES.buy}:buy.login.error.required`) });
        }
        return true;
    }), EmailValidationUtil_1.EMAIL_VALIDATION_DEBOUNCE), [customerCardKnown, skipEmailValidation, emailOrCardCodeStatus]);
    const validateLinkedEmail = (0, react_1.useCallback)((0, awesome_debounce_promise_1.default)((tmpLinkedEmail, context) => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
        if (skipEmailValidation) {
            return true;
        }
        if (!tmpLinkedEmail) {
            return context.createError({ message: t(`${i18nConstants_1.I18N_NAMESPACES.buy}:buy.login.email.error.required`) });
        }
        else if (!FormValidationUtil_1.default.isValidEmailPattern(tmpLinkedEmail)) {
            return context.createError({
                message: common_types_1.EmailValidationResponseStatus.INVALID,
            });
        }
        else if (linkedEmail !== tmpLinkedEmail) {
            const { hasIdentity } = yield getEmailStatus(tmpLinkedEmail);
            if (hasIdentity) {
                return context.createError({
                    message: t(`${i18nConstants_1.I18N_NAMESPACES.checkout}:checkout.login.error.known.user`),
                });
            }
            return yield (0, EmailValidationUtil_1.validateEmailWithBackend)(tmpLinkedEmail, context, servicesEndpoint, linkedEmailStatus, setSuggestedEmail, setLinkedEmailStatus);
        }
        return true;
    }), EmailValidationUtil_1.EMAIL_VALIDATION_DEBOUNCE), [customerCardKnown, skipEmailValidation, linkedEmail]);
    const emailValidationSchema = Yup.object({
        emailOrCardCode: Yup.string().default('').test(validateEmailOrCardCode),
        linkedEmail: customerCardKnown
            ? Yup.string().default('').test(validateLinkedEmail)
            : Yup.string().default('').optional(),
    });
    const handleEmailOrCardCodeChange = (email) => {
        setEmailOrCardCodeFieldValue(email);
        skipEmailValidation && setSkipEmailValidation(false);
        linkedEmail && setLinkedEmail('');
        customerCardInfo && setCustomerCardInfo(defaultCardInfo);
        customerCardKnown && setCustomerCardKnown(false);
    };
    const handleLinkedEmailChange = (email) => {
        setLinkedEmailFieldValue(email);
        skipEmailValidation && setSkipEmailValidation(false);
    };
    const handleIgnoreSuggestedEmailClick = () => {
        setSkipEmailValidation(true);
    };
    const handleAcceptSuggestedEmailClick = () => {
        if (customerCardKnown) {
            setLinkedEmailStatus(`${common_types_1.EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
            setLinkedEmailFieldValue(suggestedEmail);
        }
        else {
            setEmailOrCardCodeStatus(`${common_types_1.EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
            setEmailOrCardCodeFieldValue(suggestedEmail);
        }
    };
    const handleShopLoginSubmit = ({ linkedEmail, emailOrCardCode }) => {
        var _a;
        if (FormValidationUtil_1.default.isValidExploreMoreCardFormat(emailOrCardCode, checkoutLoyaltyCardPrefix).isValidFormat &&
            !linkedEmail)
            return;
        setIsLoading(true);
        if (customerCardKnown) {
            dispatch(AuthActions_creators_1.authActions.createLoginGuestAction(linkedEmail, emailOrCardCode, (_a = customerCardInfo.customerUUID) !== null && _a !== void 0 ? _a : undefined));
        }
        else {
            dispatch(AuthActions_creators_1.authActions.createLoginGuestAction(emailOrCardCode));
        }
    };
    (0, react_1.useEffect)(() => {
        if (errorFeedback && isLoading) {
            setIsLoading(false);
        }
    }, [errorFeedback]);
    (0, react_1.useEffect)(() => {
        setLinkedEmailFieldValue(linkedEmail);
    }, [linkedEmail]);
    return ((0, jsx_runtime_1.jsxs)(ShoplandingLoginForm_1.ShoplandingLoginForm, { validationSchema: emailValidationSchema, suggestedEmail: suggestedEmail, errorFeedback: errorFeedback, customerCardKnown: customerCardKnown, isLoading: isLoading, onIgnoreSuggestedEmailClick: handleIgnoreSuggestedEmailClick, onAcceptSuggestedEmailClick: handleAcceptSuggestedEmailClick, onSubmit: handleShopLoginSubmit, children: [(0, jsx_runtime_1.jsx)(ValueChangeWatcher_1.ValueChangeWatcher, { fieldName: "emailOrCardCode", value: emailOrCardCodeFieldValue, skipValidation: skipEmailValidation, onChange: handleEmailOrCardCodeChange, onError: setEmailOrCardCodeStatus }), (0, jsx_runtime_1.jsx)(ValueChangeWatcher_1.ValueChangeWatcher, { fieldName: "linkedEmail", value: linkedEmailFieldValue, skipValidation: skipEmailValidation, onChange: handleLinkedEmailChange, onError: setLinkedEmailStatus })] }));
};
exports.ShoplandingLogin = ShoplandingLogin;
